import React from 'react'
import { Button, Input, Select } from '@surecompanies/core_components'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import {
  DownloadOutlined,
  MoreOutlined,
  SearchOutlined,
  SyncOutlined,
} from '@ant-design/icons'
import { usePermission } from 'enrollment-platform/rbac/usePermission'
import { RbacActions, RbacSubjects } from 'enrollment-platform/rbac/constants'
import { sureco } from '../shared/finance.styles'
import {
  KebabButton,
  SearchInputWrap,
  PaymentTypeFilterWrap,
  StatusFilterWrap,
  ToolbarActions,
  ToolbarRow,
} from './ClientBillingProfileManagement.styles'
import { formatPaymentTypeLabel } from './bulkUpload.helpers'
import { BillingProfileStatus, PaymentType } from './types'

/** The dropdown's "no filter" entry. `Select` cannot hold `undefined`. */
const ALL_STATUSES = 'ALL'
const ALL_PAYMENT_TYPES = 'ALL'

const PAYMENT_TYPE_OPTIONS = [
  { value: ALL_PAYMENT_TYPES, label: 'All payment types' },
  ...Object.values(PaymentType).map((type) => ({
    value: type,
    label: formatPaymentTypeLabel(type),
  })),
]

interface BillingProfileToolbarProps {
  search: string
  statusFilter: BillingProfileStatus | undefined
  paymentTypeFilter: PaymentType | undefined
  /** Counts shown beside each option, from the stats endpoint. */
  statusCounts: { total: number; active: number; inactive: number }
  /** Rows the filters currently match, which is what an export would hold. */
  matchCount: number
  isExporting: boolean
  onSearchChange: (value: string) => void
  onStatusFilterChange: (value: BillingProfileStatus | undefined) => void
  onPaymentTypeFilterChange: (value: PaymentType | undefined) => void
  onOpenHubspotSync: () => void
  onOpenCreate: () => void
  onOpenUpload: () => void
  onExport: () => void
  onOpenGlobalCc: () => void
}

/**
 * Sync HubSpot is the primary action; the rest live in an overflow menu. antd
 * takes menu items as data, so each one is filtered on its own permission here
 * rather than wrapped in `RequirePermission`.
 */
const BillingProfileToolbar = ({
  search,
  statusFilter,
  paymentTypeFilter,
  statusCounts,
  matchCount,
  isExporting,
  onSearchChange,
  onStatusFilterChange,
  onPaymentTypeFilterChange,
  onOpenHubspotSync,
  onOpenCreate,
  onOpenUpload,
  onExport,
  onOpenGlobalCc,
}: BillingProfileToolbarProps) => {
  const { allowed: canManageBillingProfile } = usePermission(
    RbacActions.Manage,
    RbacSubjects.BillingProfile
  )
  const { allowed: canWriteBillingProfile } = usePermission(
    RbacActions.Write,
    RbacSubjects.BillingProfile
  )

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    onSearchChange(e.target.value)

  const handleStatusChange = (value: string) =>
    onStatusFilterChange(
      value === ALL_STATUSES ? undefined : (value as BillingProfileStatus)
    )

  const handlePaymentTypeChange = (value: string) =>
    onPaymentTypeFilterChange(
      Object.values(PaymentType).find((type) => type === value)
    )

  const statusOptions = [
    { value: ALL_STATUSES, label: `All statuses (${statusCounts.total})` },
    {
      value: BillingProfileStatus.Active,
      label: `Active (${statusCounts.active})`,
    },
    {
      value: BillingProfileStatus.Inactive,
      label: `Removed (${statusCounts.inactive})`,
    },
  ]

  const overflowItems: MenuProps['items'] = [
    ...(canWriteBillingProfile
      ? [
          {
            key: 'create',
            label: 'New company',
            onClick: onOpenCreate,
          },
          {
            key: 'upload',
            label: 'Upload Excel',
            onClick: onOpenUpload,
          },
        ]
      : []),
    ...(canManageBillingProfile
      ? [
          {
            key: 'global-cc',
            label: 'Global CC',
            onClick: onOpenGlobalCc,
          },
        ]
      : []),
  ]

  return (
    <ToolbarRow>
      <SearchInputWrap>
        <Input
          prefix={<SearchOutlined style={{ color: sureco.fg3 }} />}
          placeholder="Search company, email, or name…"
          value={search}
          onChange={handleSearchChange}
          allowClear
        />
      </SearchInputWrap>

      <StatusFilterWrap>
        <Select
          value={statusFilter ?? ALL_STATUSES}
          options={statusOptions}
          onChange={handleStatusChange}
          aria-label="Filter billing profiles by status"
        />
      </StatusFilterWrap>

      <PaymentTypeFilterWrap>
        <Select
          value={paymentTypeFilter ?? ALL_PAYMENT_TYPES}
          options={PAYMENT_TYPE_OPTIONS}
          onChange={handlePaymentTypeChange}
          aria-label="Filter billing profiles by payment type"
        />
      </PaymentTypeFilterWrap>

      <ToolbarActions>
        <Button
          size="small"
          icon={<DownloadOutlined />}
          onClick={onExport}
          loading={isExporting}
          disabled={matchCount === 0}
        >
          Export
        </Button>
        {canManageBillingProfile ? (
          <Button
            size="small"
            type="primary"
            icon={<SyncOutlined />}
            onClick={onOpenHubspotSync}
            aria-label="Sync billing profiles from HubSpot"
          >
            Sync HubSpot
          </Button>
        ) : null}
        <Dropdown menu={{ items: overflowItems }} trigger={['click']}>
          <KebabButton type="button" aria-label="More billing profile actions">
            <MoreOutlined />
          </KebabButton>
        </Dropdown>
      </ToolbarActions>
    </ToolbarRow>
  )
}

export { BillingProfileToolbar }
